import React, { ReactElement } from 'react';
import { Route, Switch } from 'react-router-dom';
import loadable from '@loadable/component';
import { routeAuthenticatorRouteProps } from '@blocs.auth';
import { useRemoveSplashLoader } from '@minecraft.utils';
import Loader from '@minecraft.loader';
import ReportLoader from 'components/Reports/common/Loader';

const GUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

const RecipientView = loadable(
  () => import(/* webpackChunkName: "cd-recipient-view" */ 'components/Recipient/RecipientView')
);
const RecipientFolderGridView = loadable(
  () => import(/* webpackChunkName: "cd-recipient-folder-grid-view" */ 'components/Recipient/RecipientFolderGridView')
);
const RecipientFolderDownloadMedias = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-recipient-folder-download-view" */ 'components/Recipient/RecipientFolderDownloadMedias'
    )
);
const DownloadAllLinkExpired = loadable(
  () => import(/* webpackChunkName: "cd-download-all-media-expired" */ 'components/DownloadAllLinkExpired')
);
const ShareSelectsRecipientView = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-share-selects-recipient-view" */ 'components/ShareSelects/ShareSelectsRecipientView'
    )
);
const ShareSelectsRecipientFolderGridView = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-share-selects-recipient-view" */ 'components/ShareSelects/ShareSelectsRecipientFolderGridView'
    )
);
const ShareSelectsRecipientFolderDetailView = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-share-selects-recipient-view" */ 'components/ShareSelects/ShareSelectsRecipientFolderDetailView'
    )
);
const Maintenance = loadable(() => import(/* webpackChunkName: "cd-maintenance" */ 'components/Maintenance'));
const PublicProfileView = loadable(
  () => import(/* webpackChunkName: "cd-profile-view" */ 'components/Profile/ProfileView'),
  { resolveComponent: (components) => components.PublicProfileViewPage }
);
const RouteAuthenticator = loadable(
  () => import(/* webpackChunkName: "cd-route-authenticator" */ 'components/RouteAuthenticator')
);
const HomePage = loadable(() => import(/* webpackChunkName: "cd-home" */ 'components/Home/HomePage'));
const Projects = loadable(() => import(/* webpackChunkName: "cd-projects" */ 'components/ProjectsList'));
const Reports = loadable(() => import(/* webpackChunkName: "cd-reports" */ 'components/Reports'));
const TalentSizeSheet = loadable(
  () => import(/* webpackChunkName: "talent-size-sheet" */ 'components/TalentSizeSheet')
);
const PublicTalentSizeSheet = loadable(
  () => import(/* webpackChunkName: "talent-size-sheet" */ 'components/TalentSizeSheet/PublicTalentSizeSheet')
);
const ReportsTypeTheatricalProject = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-reports-theatrical-project" */ 'components/Reports/ReportsType/ReportsTypeTheatricalProject'
    )
);
const ReportsTypeSchedule = loadable(
  () => import(/* webpackChunkName: "cd-reports-schedule" */ 'components/Reports/ReportsType/ReportsTypeSchedule')
);
const ReportsTypeProjectOverview = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-reports-proj-overview" */ 'components/Reports/ReportsType/ReportsTypeProjectOverview'
    )
);
const ReportsTypeListReport = loadable(
  () => import(/* webpackChunkName: "cd-reports-list-report" */ 'components/Reports/ReportsType/ReportsTypeListReport')
);
const PublicReportsTypePresentationLog = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-reports-presentation-log" */ 'components/Reports/ReportsType/ReportsTypePresentationLog'
    ),
  { resolveComponent: (components) => components.PublicReportsTypePresentationLog }
);
const ReportsTypePresentationLog = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-reports-presentation-log" */ 'components/Reports/ReportsType/ReportsTypePresentationLog'
    )
);
const ReportsTypePresentationPicks = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-reports-presentation-picks" */ 'components/Reports/ReportsType/ReportsTypePresentationPicks'
    )
);
const PublicReportsTypePresentationSizeSheets = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-reports-presentation-size-sheets" */ 'components/Reports/ReportsType/ReportsTypePresentationSizeSheets'
    ),
  { resolveComponent: (components) => components.PublicReportsTypePresentationSizeSheets }
);
const PublicFormReport = loadable(
  () => import(/* webpackChunkName: "cd-form-report" */ 'components/Forms/ExportFormResponses/FormResponseReport'),
  { resolveComponent: (components) => components.PublicFormResponseReport }
);
const ReportsTypePresentationSizeSheets = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-reports-presentation-size-sheets" */ 'components/Reports/ReportsType/ReportsTypePresentationSizeSheets'
    )
);
const ReportsTypeSessionSizeSheets = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-reports-session-size-sheet" */ 'components/Reports/ReportsType/ReportsTypeSessionSizeSheets/ReportsTypeSessionSizeSheets'
    )
);
const ReportsTypeBookingCallList = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-reports-booking-call-list" */ 'components/Reports/ReportsType/ReportsTypeBookingCallList'
    )
);
const ReportsTypeAgencyCallList = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-reports-agency-call-list" */ 'components/Reports/ReportsType/ReportsTypeAgencyCallList'
    )
);
const ReportsTypeScheduleSizeSheets = loadable(
  () =>
    import(
      /* webpackChunkName: "cd-reports-schedule-size-sheets" */ 'components/Reports/ReportsType/ReportsTypeScheduleSizeSheets'
    )
);
const FormReport = loadable(
  () => import(/* webpackChunkName: "cd-form-report" */ 'components/Forms/ExportFormResponses/FormResponseReport')
);
const ProjectCreate = loadable(() => import(/* webpackChunkName: "cd-create-project" */ 'components/project'));
const ProjectCreateWizard = loadable(
  () => import(/* webpackChunkName: "cd-create-project-wizard" */ 'components/project/ProjectCreateWizard')
);
const ProjectDetail = loadable(
  () => import(/* webpackChunkName: "cd-project-details" */ 'components/projects/ProjectDetail')
);
const EditRepresentatives = loadable(
  () => import(/* webpackChunkName: "cd-representatives-edit" */ 'components/Representatives/EditRepresentativesList')
);
const CreateRepresentatives = loadable(
  () =>
    import(/* webpackChunkName: "cd-representatives-create" */ 'components/Representatives/CreateRepresentativesList')
);
const ViewRepresentatives = loadable(
  () => import(/* webpackChunkName: "cd-representatives-view" */ 'components/Representatives/RepresentativesList')
);
const FavoritesListView = loadable(
  () => import(/* webpackChunkName: "cd-favorites-lists" */ 'components/FavoritesListView')
);
const FavoritesLists = loadable(() => import(/* webpackChunkName: "cd-favorites-lists" */ 'components/FavoritesLists'));
const AccountDetails = loadable(
  () => import(/* webpackChunkName: "cd-account-details" */ 'components/AccountSettings/AccountDetails/AccountDetails')
);
const NotificationSettings = loadable(
  () =>
    import(
      /* webpackChunkName: "casting-notification-settings" */ 'components/AccountSettings/NotificationSettings/NotificationSettings'
    )
);
const Talent = loadable(() => import(/* webpackChunkName: "cd-talent" */ 'components/Search'));
const Collaborators = loadable(() => import(/* webpackChunkName: "cd-collaborators" */ 'components/Collaborators'));
const CastItUploadLog = loadable(
  () => import(/* webpackChunkName: "cd-cast-it-upload-log" */ 'components/CastItUploadsPage')
);
const Forms = loadable(() => import(/* webpackChunkName: "cd-forms-list" */ 'components/Forms/FormsList/FormsList'));
const FormCreate = loadable(
  () => import(/* webpackChunkName: "cd-form-create" */ 'components/Forms/CreateForm/CreateForm')
);
const FormEdit = loadable(() => import(/* webpackChunkName: "cd-form-edit" */ 'components/Forms/EditForm/EditForm'));
const ProfileView = loadable(() => import(/* webpackChunkName: "cd-profile-view" */ 'components/Profile/ProfileView'));
const NotFound = loadable(() => import(/* webpackChunkName: "cd-not-found" */ 'components/NotFound/NotFoundPage'));

const TALENT_SIZE_SHEET_PATHS = [
  '/talent-size-sheet/talent/:talentId',
  '/talent-size-sheet/talent/:talentId/role/:roleId',
  '/talent-size-sheet/talent/:talentId/project/:projectId',
  '/talent-size-sheet/talent/:talentId/role/:roleId/project/:projectId',
];

const PUBLIC_TALENT_SIZE_SHEET_PATHS = [
  '/public-talent-size-sheet/talent/:talentId',
  '/public-talent-size-sheet/talent/:talentId/role/:roleId',
  '/public-talent-size-sheet/talent/:talentId/project/:projectId',
  '/public-talent-size-sheet/talent/:talentId/role/:roleId/project/:projectId',
];

const Routes = (): ReactElement => {
  useRemoveSplashLoader();

  return (
    <Switch>
      <Route exact path={`/:guid(${GUID})`}>
        <RecipientView fallback={<Loader />} />
      </Route>
      <Route exact path={`/:guid(${GUID})/folders`}>
        <RecipientFolderGridView fallback={<Loader />} />
      </Route>
      <Route exact path={`/:guid(${GUID})/folder/:folderId/group/:groupId?`}>
        <RecipientFolderGridView fallback={<Loader />} />
      </Route>
      <Route exact path="/download-all-media-expired">
        <DownloadAllLinkExpired fallback={<Loader />} />
      </Route>
      <Route exact path={`/download-all-media/:mediaGuid/:guid(${GUID})/folder/:folderId`}>
        <RecipientFolderDownloadMedias fallback={<Loader />} />
      </Route>
      <Route exact path={`/share-selects/:guid(${GUID})`}>
        <ShareSelectsRecipientView fallback={<Loader />} />
      </Route>
      <Route exact path={`/share-selects/:guid(${GUID})/folder/:folderId/talents`}>
        <ShareSelectsRecipientFolderGridView fallback={<Loader />} />
      </Route>
      <Route exact path={`/share-selects/:guid(${GUID})/folder/:folderId/talents/:talentGuid(${GUID})`}>
        <ShareSelectsRecipientFolderDetailView fallback={<Loader />} />
      </Route>
      <Route exact path="/maintenance">
        <Maintenance fallback={<Loader />} />
      </Route>
      <Route exact path="/profile-public/:guid">
        <PublicProfileView fallback={<Loader />} />
      </Route>
      <Route {...routeAuthenticatorRouteProps}>
        <RouteAuthenticator />
      </Route>
      <Route exact path="/">
        <HomePage />
      </Route>
      <Route exact path="/projects">
        <Projects fallback={<Loader />} />
      </Route>
      <Route exact path="/reports">
        <Reports fallback={<Loader />} />
      </Route>
      <Route exact path={TALENT_SIZE_SHEET_PATHS}>
        <TalentSizeSheet fallback={<Loader />} />
      </Route>
      <Route exact path={PUBLIC_TALENT_SIZE_SHEET_PATHS}>
        <PublicTalentSizeSheet fallback={<Loader />} />
      </Route>
      <Route path="/reports/theatrical-project/:reportId">
        <ReportsTypeTheatricalProject fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/reports/schedule/:scheduleId">
        <ReportsTypeSchedule fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/reports/project-overview/:reportId">
        <ReportsTypeProjectOverview fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/reports/project-list/:projectId/:listId">
        <ReportsTypeListReport fallback={<ReportLoader />} />
      </Route>
      <Route exact path={`/reports/presentation-log/:presentationId/:guid(${GUID})`}>
        <PublicReportsTypePresentationLog fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/reports/presentation-log/:presentationId">
        <ReportsTypePresentationLog fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/reports/presentation-picks/:presentationId">
        <ReportsTypePresentationPicks fallback={<ReportLoader />} />
      </Route>
      <Route exact path={`/reports/presentation-size-sheets/:presentationId/:guid(${GUID})`}>
        <PublicReportsTypePresentationSizeSheets fallback={<ReportLoader />} />
      </Route>
      <Route exact path={`/reports/form-response/:presentationId/:reportId(${GUID})`}>
        <PublicFormReport fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/reports/presentation-size-sheets/:presentationId">
        <ReportsTypePresentationSizeSheets fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/reports/session-size-sheets/:reportId">
        <ReportsTypeSessionSizeSheets fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/reports/booking-call-list/:bookingCallListId">
        <ReportsTypeBookingCallList fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/reports/agency-call-list/:agencyCallListId">
        <ReportsTypeAgencyCallList fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/reports/schedule-size-sheets/:scheduleId">
        <ReportsTypeScheduleSizeSheets fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/reports/forms/:reportId">
        <FormReport fallback={<ReportLoader />} />
      </Route>
      <Route exact path="/project/create">
        <ProjectCreate fallback={<Loader />} />
      </Route>
      <Route exact path="/project/wizard">
        <ProjectCreateWizard fallback={<Loader />} />
      </Route>
      <Route path="/project/:projectId">
        <ProjectDetail fallback={<Loader />} />
      </Route>
      <Route exact path="/representatives/edit/:listId">
        <EditRepresentatives fallback={<Loader />} />
      </Route>
      <Route exact path="/representatives/create">
        <CreateRepresentatives fallback={<Loader />} />
      </Route>
      <Route exact path="/representatives">
        <ViewRepresentatives fallback={<Loader />} />
      </Route>
      <Route exact path="/favorites-lists/:listId">
        <FavoritesListView fallback={<Loader />} />
      </Route>
      <Route exact path="/favorites-lists">
        <FavoritesLists fallback={<Loader />} />
      </Route>
      <Route exact path="/account-settings/details">
        <AccountDetails />
      </Route>
      <Route exact path="/account-settings/notifications">
        <NotificationSettings />
      </Route>
      <Route exact path="/talent">
        <Talent fallback={<Loader />} />
      </Route>
      <Route exact path="/collaborators">
        <Collaborators fallback={<Loader />} />
      </Route>
      <Route path="/cast-it-uploads">
        <CastItUploadLog fallback={<Loader />} />
      </Route>
      <Route exact path="/forms">
        <Forms fallback={<Loader />} />
      </Route>
      <Route exact path="/forms/create">
        <FormCreate fallback={<Loader />} />
      </Route>
      <Route exact path="/forms/:id/edit">
        <FormEdit fallback={<Loader />} />
      </Route>
      <Route exact path="/profile/:profileId/preview">
        <ProfileView fallback={<Loader />} />
      </Route>
      <Route path="*">
        <NotFound fallback={<Loader />} />
      </Route>
    </Switch>
  );
};

export default Routes;
